import React, { useState, useEffect } from 'react';
import DeviceFramePreview, {
  type DeviceType,
} from './DeviceFramePreview';

export interface PostPreviewPanelProps {
  /** Optional platform label shown as a chip above the device frame. */
  platformLabel?: string;
  /** Optional platform emoji/icon shown next to the label. */
  platformIcon?: string;
  /** Account/display name shown in the phone header. */
  accountName?: string;
  /** Avatar image URL. Falls back to a gradient circle. */
  avatarUrl?: string;
  /** Post caption body. */
  caption?: string;
  /** Optional hashtags to render below the caption. */
  hashtags?: string[];
  /** Primary media/image URL to render inside the frame. */
  imageUrl?: string;
  /** Media type — 'video' renders an inline player instead of an image. */
  mediaType?: 'image' | 'video';
  /** Optional footer (e.g. character counter) rendered below the device. */
  footer?: React.ReactNode;
  /**
   * Selected device for the outer frame. Defaults to ``'phone'`` — most
   * post preview call sites are narrow sidebars where phone is the
   * right default.
   */
  device?: DeviceType;
  /**
   * When provided, the device tabs render and drive this setter.
   * Omit for a locked-device preview (e.g. compact chip in a
   * multi-platform stack).
   */
  onDeviceChange?: (d: DeviceType) => void;
  /**
   * Restrict which device tabs render. Use this to hide Desktop for
   * mobile-only platforms (TikTok, Instagram feed).
   */
  availableDevices?: DeviceType[];
  /**
   * URL bar text — e.g. ``"instagram.com/your_account"``. Hidden
   * when omitted.
   */
  urlText?: string;
}

/**
 * Generic post preview panel — renders a polished phone / tablet /
 * desktop device mockup with a social-card inside (header, image,
 * engagement row, caption).
 *
 * Wraps the shared ``DeviceFramePreview`` so Studio and /compose/:id
 * get the same chrome as the Page Builder preview
 * (Phone/Tablet/Desktop tabs, realistic bezel with notch, URL bar,
 * "Made with …" style footer). Previously this
 * component rendered its own inline ``border-[10px] border-black``
 * rectangle, which was the source of the "why doesn't post preview
 * look like page preview" UX complaint.
 */
export const PostPreviewPanel: React.FC<PostPreviewPanelProps> = ({
  platformLabel,
  platformIcon,
  accountName = 'your_account',
  avatarUrl,
  caption = '',
  hashtags,
  imageUrl,
  mediaType = 'image',
  footer,
  device = 'phone',
  onDeviceChange,
  availableDevices,
  urlText,
}) => {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setAvatarFailed(false); }, [avatarUrl]);
  useEffect(() => { setImageFailed(false); }, [imageUrl]);
  const showAvatar = Boolean(avatarUrl) && !avatarFailed;
  const showImage = Boolean(imageUrl) && !imageFailed;

  const cleanHashtags = (hashtags || [])
    .map((h) => (h || '').trim())
    .filter(Boolean)
    .map((h) => (h.startsWith('#') ? h : `#${h}`));

  const headerChip = platformLabel ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60">
      {platformIcon && <span aria-hidden="true">{platformIcon}</span>}
      {platformLabel}
    </span>
  ) : undefined;

  return (
    <DeviceFramePreview
      device={device}
      onDeviceChange={onDeviceChange}
      availableDevices={availableDevices}
      headerChip={headerChip}
      urlText={urlText}
      outerFooter={footer}
    >
      {/* Social card content inside the device screen */}
      <div className="bg-white">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200">
          {showAvatar ? (
            <img
              src={avatarUrl}
              alt={accountName}
              className="w-7 h-7 rounded-full object-cover bg-slate-100"
              referrerPolicy="no-referrer"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-[10px] font-semibold">
              {(accountName || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-slate-900 truncate leading-tight">
              {accountName}
            </div>
            <div className="text-[10px] text-slate-400 leading-tight">Sponsored</div>
          </div>
          <span className="text-slate-400 text-base leading-none">…</span>
        </div>

        {/* Media — 4:5 portrait to match the feed aspect on mobile. */}
        {showImage && mediaType === 'video' ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            src={imageUrl}
            className="w-full aspect-[4/5] object-cover bg-black"
            loop
            muted
            playsInline
            preload="metadata"
            onError={() => setImageFailed(true)}
          />
        ) : showImage ? (
          <img
            src={imageUrl}
            alt="Post"
            className="w-full aspect-[4/5] object-cover bg-slate-100"
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="w-full aspect-[4/5] bg-slate-100 flex items-center justify-center">
            <span className="text-xs text-slate-400">
              {imageUrl ? 'Media unavailable' : 'No media'}
            </span>
          </div>
        )}

        {/* Engagement row */}
        <div className="px-3 py-2 flex items-center gap-3 text-slate-700 border-b border-slate-100">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
          <svg className="ml-auto" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
        </div>

        {/* Caption */}
        {(caption || cleanHashtags.length > 0) && (
          <div className="px-3 py-2.5 text-xs leading-snug text-slate-900">
            {caption && (
              <div className="whitespace-pre-wrap break-words">
                <span className="font-semibold">{accountName}</span>{' '}
                <span>{caption}</span>
              </div>
            )}
            {cleanHashtags.length > 0 && (
              <div className="mt-1.5 text-[11px] text-sky-700 break-words">
                {cleanHashtags.join(' ')}
              </div>
            )}
          </div>
        )}
      </div>
    </DeviceFramePreview>
  );
};
